import { ACCESS_ROLES, effectiveLevel, fieldTypeLabel, folderLevel } from "@/lib/forms";
import type {
  AccessLevel, AccessRole, AdminForm, AdminFormField, AdminFormSection, FormEntity,
} from "@/lib/types";

/**
 * The Custom fields page's lists (ACL-256), as GoHighLevel's: every field or every folder of the
 * contact's and the firm's forms, in one table, narrowed by object, folder, type and a search. Pure, for
 * `test/custom-fields.test.ts`; the page draws what these return.
 */

/** Which records the page lists: both kinds, or one. */
export type ObjectFilter = "all" | FormEntity;
export const OBJECTS: readonly FormEntity[] = ["lead", "firm"];

export function objectLabel(entity: FormEntity): string {
  return entity === "firm" ? "Firm" : "Contact";
}

export type Designs = Partial<Record<FormEntity, AdminForm | undefined>>;

/** Rows per page of the fields table: about GoHighLevel's page, and a folder's fields fit on one. */
export const PAGE_SIZE = 25;
/** The sizes the page offers; `all` is every row on one page. */
export const PAGE_SIZES = [25, 50, 100] as const;
export const ALL_ROWS = "all";

/** The rows per page a URL asks for: one of the sizes, every row for `all`, else the default. */
export function pageSizeOf(param: string | null): number {
  if (param === ALL_ROWS) return Number.POSITIVE_INFINITY;
  const n = Number(param);
  return (PAGE_SIZES as readonly number[]).includes(n) ? n : PAGE_SIZE;
}

/** One page of `rows`: the page asked for, held within the pages there are, so a filter that shrank the
 *  list never shows an empty page. */
export function pageOf<T>(rows: readonly T[], page: number, size = PAGE_SIZE): { page: number; rows: T[]; total: number } {
  const total = rows.length;
  const last = Math.max(0, Math.ceil(total / size) - 1);
  const current = Math.min(Math.max(0, Math.floor(page) || 0), last);
  return { page: current, rows: rows.slice(current * size, (current + 1) * size), total };
}

export interface FieldRow {
  entity: FormEntity;
  section: AdminFormSection;
  field: AdminFormField;
  /** What each role ends up with, and whether that comes from the folder. */
  levels: Record<AccessRole, AccessLevel>;
  inherited: Record<AccessRole, boolean>;
}

export interface FolderRow {
  entity: FormEntity;
  section: AdminFormSection;
  /** Fields not archived. */
  fields: number;
  levels: Record<AccessRole, AccessLevel>;
}

export interface ListFilter {
  object: ObjectFilter;
  /** Narrow to one folder of one object. */
  folder?: { entity: FormEntity; key: string } | null;
  search?: string;
  type?: string | null;
  /** List archived folders and fields instead of the active ones. */
  archived?: boolean;
}

const entitiesOf = (object: ObjectFilter): readonly FormEntity[] => (object === "all" ? OBJECTS : [object]);

/** Every field row the filter keeps, folder by folder in the form's order. An archived field is listed
 *  only when asked for archived ones, and then so is every field of an archived folder. */
export function fieldRows(designs: Designs, filter: ListFilter): FieldRow[] {
  const needle = (filter.search ?? "").trim().toLowerCase();
  const rows: FieldRow[] = [];
  for (const entity of entitiesOf(filter.object)) {
    for (const section of designs[entity]?.sections ?? []) {
      if (filter.folder && (filter.folder.entity !== entity || filter.folder.key !== section.key)) continue;
      for (const field of section.fields) {
        const archived = field.archived || section.archived;
        if (archived !== Boolean(filter.archived)) continue;
        if (filter.type && field.type !== filter.type) continue;
        if (
          needle &&
          !`${field.title} ${field.key} ${section.title} ${fieldTypeLabel(field.type)}`.toLowerCase().includes(needle)
        ) {
          continue;
        }
        const levels = {} as Record<AccessRole, AccessLevel>;
        const inherited = {} as Record<AccessRole, boolean>;
        for (const role of ACCESS_ROLES) {
          const got = effectiveLevel(field, section, role);
          levels[role] = got.level;
          inherited[role] = got.inherited;
        }
        rows.push({ entity, section, field, levels, inherited });
      }
    }
  }
  return rows;
}

/** Every folder row the filter keeps, in the form's order. */
export function folderRows(designs: Designs, filter: Pick<ListFilter, "object" | "search" | "archived">): FolderRow[] {
  const needle = (filter.search ?? "").trim().toLowerCase();
  const rows: FolderRow[] = [];
  for (const entity of entitiesOf(filter.object)) {
    for (const section of designs[entity]?.sections ?? []) {
      if (section.archived !== Boolean(filter.archived)) continue;
      if (needle && !`${section.title} ${section.key}`.toLowerCase().includes(needle)) continue;
      rows.push({
        entity,
        section,
        fields: section.fields.filter((f) => !f.archived).length,
        levels: { supervisor: folderLevel(section, "supervisor"), rep: folderLevel(section, "rep") },
      });
    }
  }
  return rows;
}

/** The counts on the object cards and the Fields / Folders switch: active ones only. */
export function counts(designs: Designs, object: ObjectFilter): { fields: number; folders: number } {
  let fields = 0;
  let folders = 0;
  for (const entity of entitiesOf(object)) {
    for (const section of designs[entity]?.sections ?? []) {
      if (section.archived) continue;
      folders += 1;
      fields += section.fields.filter((f) => !f.archived).length;
    }
  }
  return { fields, folders };
}

/** The folders a field can be moved to: the other active folders of its own object. */
export function moveTargets(design: AdminForm | undefined, from: string): AdminFormSection[] {
  return (design?.sections ?? []).filter((s) => !s.archived && s.key !== from);
}
